import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Plus, Trash2, TrendingDown, TrendingUp, Minus, Scale, Ruler } from 'lucide-react';
import { API } from '@/lib/api';
import { cn } from '@/lib/utils';
import { interpretarGrasaCorporal, interpretarMasaMuscular } from '@/lib/antropometria';
import { CalcNutricional } from './nutrition/CalcNutricional';
import { PlanAlimenticio } from './nutrition/PlanAlimenticio';
import { Recordatorio24h } from './nutrition/Recordatorio24h';
import { BitacoraSintomas } from './nutrition/BitacoraSintomas';

const TABS = [
  { key: 'mediciones',  label: 'Mediciones' },
  { key: 'calculadora', label: 'Calculadora' },
  { key: 'plan',        label: 'Plan alimenticio' },
  { key: 'recordatorio',label: 'Recordatorio 24h' },
  { key: 'sintomas',    label: 'Síntomas' },
];

const calcEdad = (fechaNac) => {
  if (!fechaNac) return null;
  const hoy = new Date();
  const nac = new Date(fechaNac);
  let edad = hoy.getFullYear() - nac.getFullYear();
  const m = hoy.getMonth() - nac.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad--;
  return edad;
};

const IMC_LABEL = (imc) => {
  if (!imc) return null;
  if (imc < 18.5) return { label: 'Bajo peso',   color: 'text-blue-500' };
  if (imc < 25)   return { label: 'Normal',       color: 'text-emerald-500' };
  if (imc < 30)   return { label: 'Sobrepeso',    color: 'text-amber-500' };
  return                  { label: 'Obesidad',    color: 'text-rose-500' };
};

const TrendIcon = ({ current, prev }) => {
  if (!prev || !current) return <Minus className="h-4 w-4 text-muted-foreground" />;
  if (current < prev) return <TrendingDown className="h-4 w-4 text-emerald-500" />;
  if (current > prev) return <TrendingUp className="h-4 w-4 text-rose-500" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
};

const emptyMedForm = {
  fecha: new Date().toISOString().split('T')[0],
  peso: '', cintura: '', cadera: '', brazo: '', muneca: '',
  masa_muscular: '', grasa_corporal: '', notas: '',
};

const MedicionesTab = ({ patient }) => {
  const [mediciones, setMediciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyMedForm);

  const fetch = async () => {
    try {
      const res = await axios.get(`${API}/patients/${patient.id}/mediciones`);
      setMediciones(res.data);
    } catch { toast.error('Error al cargar mediciones'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, [patient.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fecha) { toast.error('La fecha es obligatoria'); return; }
    const payload = {
      paciente_id: patient.id, fecha: form.fecha,
      peso:           form.peso           !== '' ? parseFloat(form.peso)           : null,
      cintura:        form.cintura        !== '' ? parseFloat(form.cintura)        : null,
      cadera:         form.cadera         !== '' ? parseFloat(form.cadera)         : null,
      brazo:          form.brazo          !== '' ? parseFloat(form.brazo)          : null,
      muneca:         form.muneca         !== '' ? parseFloat(form.muneca)         : null,
      masa_muscular:  form.masa_muscular  !== '' ? parseFloat(form.masa_muscular)  : null,
      grasa_corporal: form.grasa_corporal !== '' ? parseFloat(form.grasa_corporal) : null,
      notas: form.notas || null,
    };
    try {
      await axios.post(`${API}/patients/${patient.id}/mediciones`, payload);
      toast.success('Medición registrada');
      setDialogOpen(false);
      setForm(emptyMedForm);
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta medición? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/patients/${patient.id}/mediciones/${id}`);
      setMediciones(prev => prev.filter(m => m.id !== id));
      toast.success('Medición eliminada');
    } catch { toast.error('Error al eliminar'); }
  };

  const sexo  = patient.sexo;
  const edad  = calcEdad(patient.fecha_nacimiento);

  const latest = mediciones[mediciones.length - 1];
  const prev   = mediciones[mediciones.length - 2];

  const currentImc = latest?.peso && patient.estatura
    ? (latest.peso / Math.pow(patient.estatura / 100, 2)).toFixed(1)
    : patient.peso && patient.estatura
    ? (patient.peso / Math.pow(patient.estatura / 100, 2)).toFixed(1)
    : null;

  const imcInfo = IMC_LABEL(parseFloat(currentImc));

  const grasaInfo   = interpretarGrasaCorporal(latest?.grasa_corporal, sexo);
  const musculoInfo = interpretarMasaMuscular(latest?.masa_muscular, sexo, edad);

  const chartData = mediciones.filter(m => m.peso).map(m => ({
    fecha: m.fecha.slice(5), peso: m.peso, cintura: m.cintura, cadera: m.cadera,
  }));

  // ── Panel de progreso ──────────────────────────────────────────────────────
  const diasSinConsulta = latest?.fecha
    ? Math.floor((new Date() - new Date(latest.fecha)) / (1000 * 60 * 60 * 24))
    : null;

  const medConPeso = mediciones.filter(m => m.peso);
  const primera    = medConPeso[0];
  const ultimaPeso = medConPeso[medConPeso.length - 1];

  let deltaPeso    = null;
  let velocidad    = null;
  let deltaCintura = null;

  if (medConPeso.length >= 2) {
    deltaPeso = (primera.peso - ultimaPeso.peso).toFixed(1);          // positivo = perdió
    const dias  = Math.floor((new Date(ultimaPeso.fecha) - new Date(primera.fecha)) / (1000 * 60 * 60 * 24));
    const semanas = dias / 7;
    if (semanas > 0) velocidad = (Math.abs(deltaPeso) / semanas).toFixed(2);
  }

  const medConCintura = mediciones.filter(m => m.cintura);
  if (medConCintura.length >= 2) {
    const primC = medConCintura[0];
    const ultC  = medConCintura[medConCintura.length - 1];
    deltaCintura = (primC.cintura - ultC.cintura).toFixed(1);          // positivo = perdió
  }
  // ──────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Tarjetas resumen */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card className="bg-card border-border/50 lg:col-span-1">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Peso actual</span>
              <TrendIcon current={latest?.peso} prev={prev?.peso} />
            </div>
            <p className="text-2xl font-bold">
              {latest?.peso ?? patient.peso ?? '—'}
              <span className="text-sm font-normal text-muted-foreground ml-1">kg</span>
            </p>
            {prev?.peso && latest?.peso && (
              <p className={cn("text-xs mt-0.5", latest.peso < prev.peso ? "text-emerald-500" : "text-rose-500")}>
                {latest.peso < prev.peso ? '▼' : '▲'} {Math.abs(latest.peso - prev.peso).toFixed(1)} kg
              </p>
            )}
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50 lg:col-span-1">
          <CardContent className="p-4">
            <div className="flex items-center gap-1 mb-1">
              <Scale className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">IMC</span>
            </div>
            <p className="text-2xl font-bold">{currentImc ?? '—'}</p>
            {imcInfo && <p className={cn("text-xs mt-0.5 font-medium", imcInfo.color)}>{imcInfo.label}</p>}
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50 lg:col-span-1">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Cintura</span>
              <TrendIcon current={latest?.cintura} prev={prev?.cintura} />
            </div>
            <p className="text-2xl font-bold">
              {latest?.cintura ?? '—'}
              {latest?.cintura && <span className="text-sm font-normal text-muted-foreground ml-1">cm</span>}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50 lg:col-span-1">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Grasa corporal</span>
              <TrendIcon current={latest?.grasa_corporal} prev={prev?.grasa_corporal} />
            </div>
            <p className="text-2xl font-bold">
              {latest?.grasa_corporal ?? '—'}
              {latest?.grasa_corporal && <span className="text-sm font-normal text-muted-foreground ml-1">%</span>}
            </p>
            {grasaInfo && <p className={cn("text-xs mt-0.5 font-medium", grasaInfo.color)}>{grasaInfo.label}</p>}
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50 lg:col-span-1">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Masa muscular</span>
              <TrendIcon current={latest?.masa_muscular} prev={prev?.masa_muscular} />
            </div>
            <p className="text-2xl font-bold">
              {latest?.masa_muscular ?? '—'}
              {latest?.masa_muscular && <span className="text-sm font-normal text-muted-foreground ml-1">%</span>}
            </p>
            {musculoInfo && <p className={cn("text-xs mt-0.5 font-medium", musculoInfo.color)}>{musculoInfo.label}</p>}
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50 lg:col-span-1">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Cadera</span>
              <TrendIcon current={latest?.cadera} prev={prev?.cadera} />
            </div>
            <p className="text-2xl font-bold">
              {latest?.cadera ?? '—'}
              {latest?.cadera && <span className="text-sm font-normal text-muted-foreground ml-1">cm</span>}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ── Panel de progreso del tratamiento ── */}
      {mediciones.length > 0 && (
        <Card className="bg-card border-border/50">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">

              {/* Días desde última consulta */}
              <div className="space-y-0.5">
                <p className="text-xs text-muted-foreground">Última medición</p>
                {diasSinConsulta === null ? (
                  <p className="text-sm font-medium text-muted-foreground">—</p>
                ) : (
                  <>
                    <p className={cn(
                      'text-2xl font-bold',
                      diasSinConsulta <= 14  ? 'text-emerald-600' :
                      diasSinConsulta <= 30  ? 'text-amber-500'   : 'text-rose-500'
                    )}>
                      {diasSinConsulta}
                      <span className="text-sm font-normal text-muted-foreground ml-1">días</span>
                    </p>
                    <p className={cn(
                      'text-xs font-medium',
                      diasSinConsulta <= 14  ? 'text-emerald-600' :
                      diasSinConsulta <= 30  ? 'text-amber-500'   : 'text-rose-500'
                    )}>
                      {diasSinConsulta <= 14 ? 'Al día' : diasSinConsulta <= 30 ? 'Próxima consulta' : 'Sin consulta reciente'}
                    </p>
                  </>
                )}
              </div>

              {/* Peso total perdido/ganado */}
              <div className="space-y-0.5">
                <p className="text-xs text-muted-foreground">Desde primera consulta</p>
                {deltaPeso === null ? (
                  <p className="text-sm font-medium text-muted-foreground">Min. 2 registros</p>
                ) : (
                  <>
                    <p className={cn('text-2xl font-bold', parseFloat(deltaPeso) > 0 ? 'text-emerald-600' : parseFloat(deltaPeso) < 0 ? 'text-rose-500' : 'text-muted-foreground')}>
                      {parseFloat(deltaPeso) > 0 ? '−' : parseFloat(deltaPeso) < 0 ? '+' : ''}{Math.abs(deltaPeso)}
                      <span className="text-sm font-normal text-muted-foreground ml-1">kg</span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {parseFloat(deltaPeso) > 0 ? 'perdidos' : parseFloat(deltaPeso) < 0 ? 'ganados' : 'sin cambio'}
                    </p>
                  </>
                )}
              </div>

              {/* Velocidad promedio */}
              <div className="space-y-0.5">
                <p className="text-xs text-muted-foreground">Velocidad promedio</p>
                {velocidad === null ? (
                  <p className="text-sm font-medium text-muted-foreground">—</p>
                ) : (
                  <>
                    <p className={cn(
                      'text-2xl font-bold',
                      parseFloat(velocidad) <= 1 ? 'text-emerald-600' : 'text-amber-500'
                    )}>
                      {velocidad}
                      <span className="text-sm font-normal text-muted-foreground ml-1">kg/sem</span>
                    </p>
                    <p className={cn(
                      'text-xs font-medium',
                      parseFloat(velocidad) <= 1 ? 'text-emerald-600' : 'text-amber-500'
                    )}>
                      {parseFloat(velocidad) <= 1 ? 'Ritmo saludable' : 'Ritmo acelerado'}
                    </p>
                  </>
                )}
              </div>

              {/* Cambio en cintura */}
              <div className="space-y-0.5">
                <p className="text-xs text-muted-foreground">Cambio en cintura</p>
                {deltaCintura === null ? (
                  <p className="text-sm font-medium text-muted-foreground">Sin datos</p>
                ) : (
                  <>
                    <p className={cn('text-2xl font-bold', parseFloat(deltaCintura) > 0 ? 'text-emerald-600' : parseFloat(deltaCintura) < 0 ? 'text-rose-500' : 'text-muted-foreground')}>
                      {parseFloat(deltaCintura) > 0 ? '−' : parseFloat(deltaCintura) < 0 ? '+' : ''}{Math.abs(deltaCintura)}
                      <span className="text-sm font-normal text-muted-foreground ml-1">cm</span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {parseFloat(deltaCintura) > 0 ? 'reducidos' : parseFloat(deltaCintura) < 0 ? 'aumentados' : 'sin cambio'}
                    </p>
                  </>
                )}
              </div>

            </div>
          </CardContent>
        </Card>
      )}

      {/* Gráfica */}
      {chartData.length >= 2 && (
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Evolución de peso</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                <YAxis domain={['dataMin - 2', 'dataMax + 2']} tick={{ fontSize: 11 }} unit=" kg" />
                <Tooltip formatter={(v) => [`${v} kg`, 'Peso']} />
                <Line type="monotone" dataKey="peso" stroke="#10b981" strokeWidth={2}
                  dot={{ r: 4, fill: '#10b981' }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Historial */}
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold">Historial de mediciones</CardTitle>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm"><Plus className="h-4 w-4 mr-1" />Nueva medición</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader><DialogTitle>Registrar medición</DialogTitle></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                  <div className="space-y-1">
                    <Label>Fecha *</Label>
                    <Input type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: 'peso',           label: 'Peso (kg)',          ph: '70.5' },
                      { key: 'cintura',        label: 'Cintura (cm)',       ph: '80' },
                      { key: 'cadera',         label: 'Cadera (cm)',        ph: '95' },
                      { key: 'brazo',          label: 'Brazo (cm)',         ph: '30' },
                      { key: 'muneca',         label: 'Muñeca (cm)',        ph: '16' },
                      { key: 'masa_muscular',  label: 'Masa muscular (%)',  ph: '35' },
                    ].map(f => (
                      <div key={f.key} className="space-y-1">
                        <Label>{f.label}</Label>
                        <Input type="number" step="0.1" placeholder={f.ph}
                          value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
                      </div>
                    ))}
                    <div className="space-y-1 col-span-2">
                      <Label>Grasa corporal (%)</Label>
                      <Input type="number" step="0.1" placeholder="25"
                        value={form.grasa_corporal} onChange={e => setForm({ ...form, grasa_corporal: e.target.value })} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label>Notas</Label>
                    <Textarea rows={2} value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button type="submit" className="flex-1">Guardar</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-8">Cargando...</p>
          ) : mediciones.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Ruler className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Sin mediciones registradas</p>
            </div>
          ) : (
            <div className="space-y-2">
              {[...mediciones].reverse().map(m => {
                const imc = m.peso && patient.estatura
                  ? (m.peso / Math.pow(patient.estatura / 100, 2)).toFixed(1) : null;
                const imcI   = IMC_LABEL(parseFloat(imc));
                const grasaI = interpretarGrasaCorporal(m.grasa_corporal, sexo);
                const muscI  = interpretarMasaMuscular(m.masa_muscular, sexo, edad);
                return (
                  <div key={m.id} className="flex items-start gap-3 p-3 rounded-lg border border-border/50 hover:bg-muted/30 transition-colors">
                    <div className="text-xs text-muted-foreground w-16 pt-0.5 shrink-0">{m.fecha}</div>
                    <div className="flex-1 flex flex-wrap gap-2">
                      {m.peso           && <Badge variant="outline" className="text-xs">⚖️ {m.peso} kg</Badge>}
                      {imc              && <Badge variant="outline" className={cn("text-xs", imcI?.color)}>IMC {imc}</Badge>}
                      {m.cintura        && <Badge variant="outline" className="text-xs">📏 Cint {m.cintura} cm</Badge>}
                      {m.cadera         && <Badge variant="outline" className="text-xs">📏 Cad {m.cadera} cm</Badge>}
                      {m.brazo          && <Badge variant="outline" className="text-xs">💪 Brazo {m.brazo} cm</Badge>}
                      {m.muneca         && <Badge variant="outline" className="text-xs">⌚ Muñeca {m.muneca} cm</Badge>}
                      {m.grasa_corporal && (
                        <Badge variant="outline" className={cn("text-xs", grasaI?.color)}>
                          🔥 {m.grasa_corporal}% grasa{grasaI ? ` · ${grasaI.label}` : ''}
                        </Badge>
                      )}
                      {m.masa_muscular && (
                        <Badge variant="outline" className={cn("text-xs", muscI?.color)}>
                          💪 {m.masa_muscular}% músculo{muscI ? ` · ${muscI.label}` : ''}
                        </Badge>
                      )}
                      {m.notas && <p className="w-full text-xs text-muted-foreground mt-1">{m.notas}</p>}
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDelete(m.id)} aria-label="Eliminar medición">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
};

export const NutritionTracker = ({ patient, onEditarPerfil }) => {
  const storageKey = `nutri-tab-${patient.id}`;
  const [activeTab, setActiveTab] = useState(
    () => sessionStorage.getItem(storageKey) || 'mediciones'
  );

  const changeTab = (tab) => {
    setActiveTab(tab);
    sessionStorage.setItem(storageKey, tab);
  };

  return (
    <div className="space-y-4">
      {/* Nav interno */}
      <div className="flex overflow-x-auto gap-1 pb-1">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => changeTab(t.key)}
            className={cn(
              'shrink-0 px-4 py-2 rounded-lg text-sm transition-colors',
              activeTab === t.key
                ? 'bg-emerald-600 text-white font-medium'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'mediciones'   && <MedicionesTab patient={patient} />}
      {activeTab === 'calculadora'  && <CalcNutricional patient={patient} onEditarPerfil={onEditarPerfil} />}
      {activeTab === 'plan'         && <PlanAlimenticio patient={patient} />}
      {activeTab === 'recordatorio' && <Recordatorio24h patient={patient} />}
      {activeTab === 'sintomas'     && <BitacoraSintomas patient={patient} />}
    </div>
  );
};
